import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowsClockwise,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  ListBullets,
} from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { DebouncedSearchInput } from '@clay-run/glaze/debounced-search-input';
import { Heading } from '@clay-run/glaze/heading';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import { useAppBasePath } from '../../_shared/base-path';
import { formatEventDate, todayIso } from '../dates';
import {
  HOME_LOOKBACK_DAYS,
  anchorMonthKey,
  homeCampaignWindow,
  shiftViewMonth,
  viewMonthFromIso,
  type ViewMonth,
} from '../home-window';
import { useLauncherCampaigns } from '../launcher-api';
import { useSyncStatus, useTriggerCampaignSync } from '../launcher-api';
import type { Campaign } from '../../_shared/launcher-campaign.types';
import { usePlaybooksLibrary } from '../use-playbooks-library';
import { usePlaysLibrary } from '../use-plays-library';
import { mergePlaybookNames, playsByCampaign } from '../plays-logic';
import { launcherStatusBadgeStatus, subtypeBadgeColor } from '../subtype-badge-colors';
import { CalendarMonthGrid } from './CalendarMonthGrid';
import { CreatePlaySidebar } from './CreatePlaySidebar';
import type { PlaybooksPlaySummary } from '../schemas';
import { PlaysActions } from './plays-actions';
import { useGuardedFlowEntry } from './UnsavedWorkGuard';

/**
 * Launcher V2 campaigns home (plan U2). List tab is the default (Key
 * Decision 11 / Decision 12 flips v1's calendar-default); Calendar is the
 * second tab. Reuses v1's DATA hooks (`useLauncherCampaigns`, `useSyncStatus`,
 * `useTriggerCampaignSync`) directly rather than copying them — they are
 * plain TanStack Query wrappers around the shared `/api/tools/launcher/*`
 * endpoints with zero rendering coupling, so importing them keeps the query
 * cache shared with v1 instead of forking a second cache for the same data
 * (Key Decision 3 ports the *logic*; these hooks have no v1 render-layer
 * logic to fork). The render layer below is entirely new, on Glaze.
 */

// View-toggle persistence (Key Decision 11)

/**
 * NEW key, deliberately distinct from v1's `launcher_list_view` (whose values
 * are `'calendar' | 'table'`, calendar-default). Sharing a key would let a
 * stored v1 `'table'` value corrupt v2 state on a machine that has used both.
 */
export const HOME_VIEW_STORAGE_KEY = 'launcher_v2_home_view';

export type PlaybooksHomeView = 'list' | 'calendar';

/** Default `'list'` (Decision 12) — any value other than the literal `'calendar'` falls back to it. */
export function loadPersistedHomeView(): PlaybooksHomeView {
  try {
    return localStorage.getItem(HOME_VIEW_STORAGE_KEY) === 'calendar' ? 'calendar' : 'list';
  } catch {
    return 'list';
  }
}

export function persistHomeView(view: PlaybooksHomeView): void {
  try {
    localStorage.setItem(HOME_VIEW_STORAGE_KEY, view);
  } catch {
    // localStorage unavailable (private mode, quota) — persistence degrades
    // to per-session state only, not a crash.
  }
}

// Month-grouping (Key Decision 3 — ported from `CampaignTable`'s monthKey/monthLabel)

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Campaigns synced from Salesforce may have no StartDate. */
const NO_DATE_KEY = '9999-99';

export function monthKey(iso: string | null | undefined): string {
  if (!iso) return NO_DATE_KEY;
  const [year, month] = iso.split('-').map(Number);
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function monthLabel(key: string): string {
  if (key === NO_DATE_KEY) return 'No Date';
  const [year, month] = key.split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Per-row Plays (QA round 7, U1, item 1 — one read for the whole list)

interface CampaignPlaysSectionProps {
  campaign: Campaign;
  plays: PlaybooksPlaySummary[];
  playsLoading: boolean;
  playsError: boolean;
  playbookNameById: Map<string, string>;
  basePath: string;
  onCreatePlay: (campaign: Campaign) => void;
  creatingPlay: boolean;
  onOpen: (href: string) => void;
}

/**
 * The per-row plays list (locked prototype removals applied): each play gets
 * its own line with a link into `v2/play/:playId`, the "Driven by" label and
 * the "Switch playbook" button are both dropped (locked removals), and
 * "Create Play" is now ALWAYS available on every row — not only when a
 * campaign has zero plays — per Spencer's 2026-08-27 decision update
 * superseding the plan text (campaigns can have multiple simultaneous active
 * plays).
 *
 * QA round 7, U1 (item 1) — no fetch here at all any more: `plays` /
 * `playsLoading` / `playsError` all come from the ONE `usePlaysLibrary()` read
 * `PlaybooksHome` does for the whole list (`playsByCampaign`). Before this
 * round each row's plays fetch was gated on a visibility hook (a
 * mitigation, not a fix — 2026-09-11 QA round 2, bug 2) because List view
 * renders every filtered campaign's row unconditionally (no
 * pagination/virtualization) and an always-on per-row fetch fired one
 * request per row on mount, tripping Terra's own `/api/tools` rate limiter
 * (`miniAppRateLimiter`, 300/min per user per app in prod) at real campaign
 * counts — not a Salesforce limit, as `plays-logic.ts#playsByCampaign`'s doc
 * explains. There is now nothing left to gate: the single batch read has
 * already resolved (or failed, or is loading) by the time any row renders.
 */
function CampaignPlaysSection({
  campaign,
  plays,
  playsLoading,
  playsError,
  playbookNameById,
  basePath,
  onCreatePlay,
  creatingPlay,
  onOpen,
}: CampaignPlaysSectionProps) {
  if (playsLoading) {
    return (
      <div className="flex items-center gap-2">
        <LoadingSpinner size="xs" />
        <Text size="xs" variant="tertiary">Loading plays…</Text>
      </div>
    );
  }

  if (playsError) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Text size="xs" variant="critical">Couldn't load plays for this campaign.</Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {plays.length === 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge>No Playbook</Badge>
        </div>
      )}
      <PlaysActions
        plays={plays}
        campaign={campaign}
        playbookNameById={playbookNameById}
        basePath={basePath}
        onCreatePlay={onCreatePlay}
        creatingPlay={creatingPlay}
        onOpen={onOpen}
      />
    </div>
  );
}

// Page

export function PlaybooksHome() {
  const basePath = useAppBasePath();

  const [homeView, setHomeView] = useState<PlaybooksHomeView>(loadPersistedHomeView);
  const [search, setSearch] = useState('');
  const [createPlayTarget, setCreatePlayTarget] = useState<Campaign | null>(null);

  // QA round 8, U1 (item 5) — ONE state value for the calendar's visible
  // month, replacing the paired `viewYear`/`viewMonth`. The old pair had
  // `prevMonth`/`nextMonth` call `setViewYear` from inside a `setViewMonth`
  // updater; under StrictMode's dev double-invoke that nested call ran
  // twice, jumping the year by 2 across a December↔January rollover.
  // `shiftViewMonth` is a single pure updater so there is nothing left to
  // double-run.
  const [view, setViewMonth] = useState<ViewMonth>(() => viewMonthFromIso(todayIso()));

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const didInitialAnchorRef = useRef(false);

  // QA round 8 Codex fixes, F3 — `today` is STATE, not a value re-read from
  // the wall clock inside the window memo on every render. A tab left open
  // past midnight kept yesterday's 14-day cutoff even after pressing
  // "Today" (the memo's deps never named a date, so nothing invalidated
  // it). `goToday` and the List-mount reset effect below refresh it.
  const [today, setToday] = useState(todayIso);

  const { campaigns, isLoading, isError } = useLauncherCampaigns();
  const { syncStatus } = useSyncStatus();
  const triggerSync = useTriggerCampaignSync();
  const library = usePlaybooksLibrary();
  // QA round 7, U1 (items 1, 3) — the ONE plays read for every list surface
  // (Home rows, the Calendar popover, the Library section). Replaces the
  // per-row `usePlaysForCampaign` fan-out and `usePlaybookNamesById`'s
  // library-only map (which missed archived playbooks — see
  // `mergePlaybookNames`'s doc).
  const playsLibrary = usePlaysLibrary();
  const playbookNameById = useMemo(
    () => mergePlaybookNames(library.data?.playbooks, playsLibrary.data?.playbookNames),
    [library.data, playsLibrary.data],
  );
  const playsByCampaignId = useMemo(
    () => playsByCampaign(playsLibrary.data?.campaigns ?? []),
    [playsLibrary.data],
  );

  // Campaigns fetch failure renders the inline error state below (the
  // `Alert` in the main content area). No custom toast here: `campaigns`
  // comes from `useLauncherCampaigns` (`../../hooks/useLauncherCampaigns`)
  // and `triggerSync` from `useTriggerCampaignSync`
  // (`../../hooks/useCampaignTypeConfig`) — both SHARED with the v1
  // launcher — so this file cannot set `suppressErrorToast` inside those
  // shared hook definitions without silently changing v1's toast behavior
  // too. The fix here is simply to stop adding a second, v2-local toast on
  // top of Terra's global React Query error handler
  // (`web/app-home/src/lib/mutation-cache-handler.ts`), which already fires
  // one for any unhandled query/mutation error (Codex adversarial-review
  // fix — was showing two toasts, in two different visual systems, per
  // failure).
  const handleViewChange = useCallback((next: PlaybooksHomeView) => {
    setHomeView(next);
    persistHomeView(next);
  }, []);

  const prevMonth = useCallback(() => {
    setViewMonth((v) => shiftViewMonth(v, -1));
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((v) => shiftViewMonth(v, 1));
  }, []);

  /** Takes a month KEY (`YYYY-MM`, or `9999-99` for undated) — a no-op for `null` (nothing to scroll to). */
  const scrollToMonth = useCallback((monthKeyToScroll: string | null) => {
    if (!monthKeyToScroll) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`v2-month-${monthKeyToScroll}`);
      if (el && listContainerRef.current) {
        const container = listContainerRef.current;
        // QA round 8 Codex fixes, F2 — before this fix the initial-anchor
        // effect always consumed its ran-flag against an empty `grouped`
        // (see below), so `monthKeyToScroll` was always `null` and this
        // branch never ran in ANY test that doesn't click "Today" — several
        // Playbooks Home test files render the List view with real
        // campaigns but never stub `Element.prototype.scrollTo` (jsdom does
        // not implement it). Fixing the anchor bug makes this branch run
        // for the first time in those files, and an unstubbed jsdom
        // `Element` throws `container.scrollTo is not a function`. Guard on
        // the method actually existing — a no-op in every real browser,
        // where `scrollTo` is always present — rather than expanding this
        // unit's scope to stub it in five unrelated test files.
        if (typeof container.scrollTo === 'function') {
          container.scrollTo({ top: el.offsetTop - container.offsetTop, behavior: 'smooth' });
        }
      }
    });
  }, []);

  // QA round 8, U1 (items 1, 5) — the windowed List: campaigns dated
  // today−14 days onward plus undated ones, widening to everything when
  // `search` is non-empty. Calendar view is unchanged (§U1b) — it keeps
  // reading every search-matched campaign regardless of date, since a rep
  // can still navigate its prev/next arrows to a month before the window.
  const { campaigns: windowedCampaigns, windowed, cutoffIso } = useMemo(
    () => homeCampaignWindow(campaigns, search, today),
    [campaigns, search, today],
  );

  const calendarCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns
      .filter((c) => {
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          c.type.toLowerCase().includes(q) ||
          (c.subtype ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.event_date ?? '').localeCompare(b.event_date ?? ''));
  }, [campaigns, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, Campaign[]>();
    for (const c of windowedCampaigns) {
      const mk = monthKey(c.event_date);
      const arr = map.get(mk) ?? [];
      arr.push(c);
      map.set(mk, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [windowedCampaigns]);

  const goToday = useCallback(() => {
    const iso = todayIso();
    setToday(iso);
    setViewMonth(viewMonthFromIso(iso));
    scrollToMonth(
      anchorMonthKey(
        grouped.map(([mk]) => mk),
        iso,
      ),
    );
  }, [grouped, scrollToMonth]);

  // Initial-anchor effect: opens the List with the current month's header at
  // the top (or the first month on/after today) instead of the oldest month.
  // Runs once per List "mount" — i.e. once per transition into `homeView ===
  // 'list'` — not on every `grouped` change thereafter (typing in Search
  // reshuffles `grouped` on every keystroke; re-anchoring on each keystroke
  // would fight the rep's own scroll position). The ran-flag is reset at the
  // TOP of this effect (root CLAUDE.md's StrictMode-Resilient Effects rule) —
  // a cleanup-only reset would leave it stuck after StrictMode's dev
  // setup→cleanup→setup double-invoke on the real mount. It also refreshes
  // `today` (QA round 8 Codex fixes, F3) so re-entering List after midnight
  // moves the 14-day cutoff forward, not just the "Today" button.
  useEffect(() => {
    didInitialAnchorRef.current = false;
    setToday(todayIso());
  }, [homeView === 'list']);

  useEffect(() => {
    // QA round 8 Codex fixes, F2 — `isLoading` must gate the ran-flag: this
    // effect used to run on the FIRST render of the List view, when
    // `campaigns` (and so `grouped`) is still `[]` because the fetch hasn't
    // resolved yet. `anchorMonthKey([], …)` is `null` (a no-op scroll), and
    // the ran-flag was already consumed — so the real list, once it
    // arrived, never got anchored to the current month.
    if (homeView !== 'list' || didInitialAnchorRef.current || isLoading) return;
    didInitialAnchorRef.current = true;
    scrollToMonth(
      anchorMonthKey(
        grouped.map(([mk]) => mk),
        today,
      ),
    );
  }, [grouped, homeView, isLoading, scrollToMonth, today]);

  const handleCreatePlay = useCallback((campaign: Campaign) => setCreatePlayTarget(campaign), []);

  /**
   * QA round 7, U2 (item 2) — the one seam Home's navigations go through.
   * Home owns no edits of its own (`dirty: false`); `useGuardedFlowEntry`
   * still asks before this mount would wipe a DIFFERENT flow's surviving
   * drafts (`PlayFlowShell`'s context-change clear) — the case neither Home
   * nor Config warned about before this unit.
   */
  const handleOpen = useGuardedFlowEntry({ dirty: false });

  const handlePlayCreated = useCallback(
    ({ playId, campaignSfdcId }: { playId: string; campaignSfdcId: string }) => {
      setCreatePlayTarget(null);
      handleOpen(`${basePath}/play/${playId}?campaignSfdcId=${encodeURIComponent(campaignSfdcId)}`);
    },
    [basePath, handleOpen],
  );

  const handleTriggerSync = useCallback(() => {
    // No custom onError toast here — see the comment above `isError`'s
    // former toast effect. The global handler already covers this mutation.
    triggerSync.mutate(undefined);
  }, [triggerSync]);

  return (
    <div className="flex-1 px-8 py-8">
      <div className="mb-2">
        <Heading size="xl">Campaigns</Heading>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <SegmentedControl value={homeView} onValueChange={(next) => handleViewChange(next)}>
          <SegmentedControlOption value="list">
            <Icon type={ListBullets} /> List
          </SegmentedControlOption>
          <SegmentedControlOption value="calendar">
            <Icon type={CalendarBlank} /> Calendar
          </SegmentedControlOption>
        </SegmentedControl>

        <DebouncedSearchInput
          aria-label="Search campaigns"
          placeholder="Search campaigns…"
          initialValue={search}
          onSearch={setSearch}
          className="w-64"
        />

        <div className="ml-auto flex items-center gap-2">
          <IconButton
            icon={ArrowsClockwise}
            variant="secondary"
            size="sm"
            aria-label="Sync campaigns from Salesforce"
            title={
              syncStatus?.campaign_hourly?.completed_at
                ? `Last synced ${formatRelativeTime(syncStatus.campaign_hourly.completed_at)}`
                : 'Sync campaigns from Salesforce'
            }
            loading={triggerSync.isPending}
            onClick={handleTriggerSync}
          />
          <IconButton icon={CaretLeft} variant="secondary" size="sm" aria-label="Previous month" onClick={prevMonth} />
          <Button variant="secondary" size="sm" onClick={goToday}>
            Today
          </Button>
          <IconButton icon={CaretRight} variant="secondary" size="sm" aria-label="Next month" onClick={nextMonth} />
        </div>
      </div>

      {homeView === 'list' && windowed && !isLoading && !isError && (
        <Text size="xs" variant="tertiary" className="mt-2 block">
          Showing events from {formatEventDate(cutoffIso)} onward — search to see older campaigns.
        </Text>
      )}

      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-border-primary bg-bg-primary p-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : isError ? (
          <Alert status="error">Failed to load campaigns. Please try again.</Alert>
        ) : homeView === 'calendar' ? (
          calendarCampaigns.length === 0 ? (
            <Text size="sm" variant="tertiary" className="block py-16 text-center">
              No campaigns match your search.
            </Text>
          ) : (
            <CalendarMonthGrid
              campaigns={calendarCampaigns}
              viewYear={view.year}
              viewMonth={view.month0}
              playbookNameById={playbookNameById}
              playsByCampaignId={playsByCampaignId}
              playsLoading={playsLibrary.isLoading}
              playsError={playsLibrary.isError}
              basePath={basePath}
              onCreatePlay={handleCreatePlay}
              creatingPlayId={createPlayTarget?.id ?? null}
              onOpen={handleOpen}
            />
          )
        ) : windowedCampaigns.length === 0 ? (
          <Text size="sm" variant="tertiary" className="block py-16 text-center">
            {search
              ? 'No campaigns match your search.'
              : `No upcoming campaigns in the last ${HOME_LOOKBACK_DAYS} days or later — search to see older campaigns.`}
          </Text>
        ) : (
          <div
            ref={listContainerRef}
            className="max-h-[calc(100vh-280px)] overflow-y-auto rounded-lg border border-border-primary bg-bg-primary"
          >
            {grouped.map(([mk, monthCampaigns]) => (
              <div key={mk}>
                <div id={`v2-month-${mk}`} className="sticky top-0 z-[5] bg-bg-primary px-4 pt-3 pb-2">
                  <div className="flex items-center gap-3">
                    <Text size="xs" weight="medium" variant="tertiary" className="whitespace-nowrap uppercase tracking-wider">
                      {monthLabel(mk)}
                    </Text>
                    <div className="h-px flex-1 bg-border-secondary" />
                    <Text size="xs" variant="tertiary">{monthCampaigns.length}</Text>
                  </div>
                </div>
                <div className="divide-y divide-border-secondary">
                  {monthCampaigns.map((campaign) => (
                    <div key={campaign.id} className="flex flex-col gap-2 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Text size="sm" weight="semibold" className="min-w-0 truncate">
                          {campaign.name}
                        </Text>
                        <Badge color={subtypeBadgeColor(campaign.subtype)}>
                          {campaign.subtype ?? campaign.type}
                        </Badge>
                        <StatusBadge status={launcherStatusBadgeStatus(campaign.launcher_status)}>
                          {campaign.launcher_status === 'launched' ? 'Launched' : 'Draft'}
                        </StatusBadge>
                        <Badge>{formatEventDate(campaign.event_date) ?? 'No date'}</Badge>
                        <Badge>{campaign.member_count.toLocaleString()} members</Badge>
                      </div>
                      <CampaignPlaysSection
                        campaign={campaign}
                        plays={playsByCampaignId.get(campaign.id) ?? []}
                        playsLoading={playsLibrary.isLoading}
                        playsError={playsLibrary.isError}
                        playbookNameById={playbookNameById}
                        basePath={basePath}
                        onCreatePlay={handleCreatePlay}
                        creatingPlay={createPlayTarget?.id === campaign.id}
                        onOpen={handleOpen}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createPlayTarget && (
        <CreatePlaySidebar
          key={createPlayTarget.id}
          campaign={createPlayTarget}
          onClose={() => setCreatePlayTarget(null)}
          onCreated={handlePlayCreated}
        />
      )}
    </div>
  );
}
